import { Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { WeekReport } from '@/api/generated/models';
import type { StatusHistoryEntry } from '@/features/history/statusHistoryTypes';
import { WeekReportStatus } from '@/api/generated/models/weekReportStatus';
import {
  useGetWeekReport,
  useGetWeekReportHistory
} from '@/api/generated/week-reports/week-reports';
import { BackButton, ContentCard } from '@/components/common';
import { StatusHistoryTable } from '@/features/history/components/StatusHistoryTable';
import { formatWeekLabel, statusConfig } from '@/features/week-reports/weekReportDisplay';

export const WeekReportHistoryPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: wrData, isLoading: isLoadingWr } = useGetWeekReport(id ?? '', {
    query: { enabled: !!id }
  });
  const weekReport = wrData as WeekReport | undefined;
  const { data: historyData, isLoading: isLoadingHistory } = useGetWeekReportHistory(id ?? '', {
    query: { enabled: !!id }
  });
  const entries = (historyData as StatusHistoryEntry[] | undefined) ?? [];

  const resolveStatus = (raw: string) => {
    const config = statusConfig[raw as WeekReportStatus];
    return config
      ? { label: t(config.labelKey), bg: config.bg, fg: config.fg }
      : { label: raw, bg: 'var(--color-bg-surface-2)', fg: 'var(--color-text-secondary)' };
  };

  if (isLoadingWr || isLoadingHistory || !weekReport) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={`/weekly-reports/${id}`} />
        <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
          {formatWeekLabel(weekReport.weekNumber, weekReport.reportYear)}
        </Text>
        <ContentCard>
          <div style={{ padding: 20 }}>
            <StatusHistoryTable entries={entries} resolveStatus={resolveStatus} />
          </div>
        </ContentCard>
      </Stack>
    </Container>
  );
};
